import * as THREE from 'three';
import { TrackRuntime } from '../core/track/runtime';
import { PrintAtom, PrintJob } from '../core/print/types';

const PART_COLORS = [
  0x4fc3f7, 0xff8a65, 0x81c784, 0xce93d8, 0xffd54f, 0x90a4ae, 0xf06292, 0x4db6ac,
];

const GOLDEN_RATIO = 0.618033988749895;
const atomHue = new THREE.Color();

export type PrintPreviewKind = 'chunks' | 'parts';

/** Colour-coded print chunks or split parts + cut planes for the viewport overlay. */
export class PrintPreview {
  readonly group = new THREE.Group();
  private partMeshes = new Map<string, THREE.Mesh>();
  private cutMeshes: THREE.Mesh[] = [];
  private highlightId: string | null = null;
  private hiddenParts = new Set<string>();
  private show = false;
  private kind: PrintPreviewKind = 'chunks';
  private raycaster = new THREE.Raycaster();
  private ndc = new THREE.Vector2();

  constructor() {
    this.group.name = 'printPreview';
    this.group.visible = false;
  }

  setVisible(on: boolean): void {
    this.show = on;
    this.group.visible = on;
  }

  get visible(): boolean {
    return this.show;
  }

  get previewKind(): PrintPreviewKind {
    return this.kind;
  }

  setPreviewKind(kind: PrintPreviewKind): void {
    this.kind = kind;
  }

  /** Show or hide a print chunk (and every split part packed into it). */
  setPartVisible(partId: string, visible: boolean): void {
    if (visible) this.hiddenParts.delete(partId);
    else this.hiddenParts.add(partId);
    for (const mesh of this.partMeshes.values()) {
      if (mesh.userData?.partId === partId) mesh.visible = visible;
    }
  }

  /** Clear per-part visibility overrides (all parts visible again). */
  clearPartVisibility(): void {
    this.hiddenParts.clear();
    for (const mesh of this.partMeshes.values()) mesh.visible = true;
  }

  /** Ray-pick a print part under the cursor. Returns the packed chunk id or null. */
  pickAtScreen(
    camera: THREE.Camera,
    clientX: number,
    clientY: number,
    canvas: HTMLElement,
  ): string | null {
    if (!this.show || this.partMeshes.size === 0) return null;
    const rect = canvas.getBoundingClientRect();
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.ndc, camera);
    const meshes = [...this.partMeshes.values()].filter((m) => m.visible);
    const hits = this.raycaster.intersectObjects(meshes, false);
    for (const hit of hits) {
      const id = hit.object.userData?.partId;
      if (typeof id === 'string') return id;
    }
    return null;
  }

  clear(): void {
    for (const mesh of this.partMeshes.values()) {
      this.group.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.partMeshes.clear();
    for (const mesh of this.cutMeshes) {
      this.group.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.cutMeshes = [];
    this.highlightId = null;
  }

  rebuild(job: PrintJob | null, runtime: TrackRuntime): void {
    const keepHighlight = this.highlightId;
    this.clear();
    if (!job) {
      this.hiddenParts.clear();
      this.group.visible = false;
      return;
    }

    // Drop hide flags for parts that no longer exist after a regenerate.
    const ids = new Set(job.parts.map((p) => p.id));
    for (const id of [...this.hiddenParts]) {
      if (!ids.has(id)) this.hiddenParts.delete(id);
    }

    const useAtoms = this.kind === 'parts' && job.atoms.length > 0;
    const items: PrintAtom[] = useAtoms
      ? job.atoms
      : job.parts.map((p) => ({ id: p.id, chunkId: p.id, kind: p.kind, mesh: p.mesh }));

    items.forEach((item, i) => {
      const color = useAtoms ? atomColor(i) : PART_COLORS[i % PART_COLORS.length];
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(item.mesh.positions, 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(item.mesh.normals, 3));
      geo.setIndex(new THREE.BufferAttribute(item.mesh.indices, 1));
      const mat = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.45,
        metalness: 0.25,
        transparent: true,
        opacity: 0.72,
        emissive: new THREE.Color(0x000000),
        emissiveIntensity: 0,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.userData = { partId: item.chunkId, atomId: item.id };
      mesh.visible = !this.hiddenParts.has(item.chunkId);
      this.group.add(mesh);
      this.partMeshes.set(item.id, mesh);
    });

    const cutGeo = new THREE.CircleGeometry(1.6, 24);
    for (const s of job.cuts) {
      const f = runtime.frameAt(s);
      const mat = new THREE.MeshBasicMaterial({
        color: 0xffeb3b,
        transparent: true,
        opacity: 0.45,
        side: THREE.DoubleSide,
        depthTest: true,
      });
      const mesh = new THREE.Mesh(cutGeo, mat);
      mesh.position.copy(f.railPos);
      const quat = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 0, 1),
        f.tan.clone().normalize(),
      );
      mesh.quaternion.copy(quat);
      mesh.renderOrder = 12;
      this.group.add(mesh);
      this.cutMeshes.push(mesh);
    }

    this.group.visible = this.show;
    if (keepHighlight) this.setHighlight(keepHighlight);
  }

  setHighlight(partId: string | null): void {
    this.highlightId = partId;
    for (const mesh of this.partMeshes.values()) {
      const mat = mesh.material as THREE.MeshStandardMaterial;
      const on = mesh.userData?.partId === partId;
      mat.emissive.setHex(on ? 0xffffff : 0x000000);
      mat.emissiveIntensity = on ? 0.35 : 0;
      mat.opacity = on ? 0.95 : 0.72;
      mat.needsUpdate = true;
    }
  }

  getHighlight(): string | null {
    return this.highlightId;
  }
}

function atomColor(i: number): number {
  return atomHue.setHSL((i * GOLDEN_RATIO) % 1, 0.62, 0.56).getHex();
}
